<script setup lang="ts">
import { ref } from 'vue';
import { VueperSlides, VueperSlide } from 'vueperslides';
import type { ComponentPublicInstance } from 'vue';
import CarouselControls from '@/components/shared/CarouselControls.vue';
import ArchiveControls from '@/components/shared/ArchiveControls.vue';
import { YoutubeIframe } from '@vue-youtube/component';
import { vueVimeoPlayer } from 'vue-vimeo-player';
import VideoControls from '@/components/shared/VideoControls.vue';
import { useInstitutionStore } from '@/stores/institution';

const institutionStore = useInstitutionStore();
const vueperSlides = ref<VueperSlidesInstance>();
const autoPlaying = ref(false);
const internalAutoPlaying = ref(false);
const sliderReady = ref(false);
const youtube = ref<YoutubeInstance[]>([]);
const vimeo = ref<VimeoInstance[]>();
const dreambroker = ref<HTMLIFrameElement>();
const currentVideoProvider = ref<Video['provider']>();
const currentSlide = ref<number>();

const emit = defineEmits(['archive-add', 'archive-remove', 'close-preview']);

interface Props {
  videos: Video[];
  selectedItemIndex: number;
  allowArchiveRemove: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  videos: () => [],
  selectedItemIndex: 0,
  allowArchiveRemove: false,
});

const isPlaying = ref(true);

type YoutubeInstance = {
  instance: {
    playVideo(): void;
    pauseVideo(): void;
    stopVideo(): void;
  };
};

type VimeoInstance = {
  play(): void;
  pause(): void;
  /* eslint-disable-next-line no-unused-vars */
  update(videoId: number): void;
};

type VideoPlayerVars = {
  cc_load_policy: 0 | 1;
  cc_lang_pref: string;
  autoplay: 0 | 1;
  modestbranding: 0 | 1;
  rel: 0 | 1;
  fs: 0 | 1;
  playsinline: 0 | 1;
};

interface Video {
  id: string;
  url: string;
  provider: 'YOUTUBE' | 'VIMEO' | 'DREAMBROKER';
}

interface VueperSlidesInstance extends ComponentPublicInstance {
  next: () => void;
  previous: () => void;
  // eslint-disable-next-line no-unused-vars
  goToSlide: (index: number) => void;
  currentSlide: { index: number };
}

interface SlideEvent {
  currentSlide: { index: number };
}

const playerVars: VideoPlayerVars = {
  cc_load_policy: 1,
  cc_lang_pref: institutionStore.locale || 'da',
  autoplay: 1,
  modestbranding: 1,
  rel: 0,
  fs: 1,
  playsinline: 1,
};

type VideoEvent = 'playVideo' | 'pauseVideo' | 'stopVideo';

async function handleVideoEvent(event: VideoEvent) {
  if (!event) return;

  const slideIndex = currentSlide.value ?? props.selectedItemIndex;

  isPlaying.value = event === 'playVideo';

  switch (currentVideoProvider.value) {
    case 'YOUTUBE':
      if (!youtube.value.length) return;
      const youtubeInstance = youtube.value[0];
      youtubeInstance?.instance?.[event]?.();
      break;
    case 'VIMEO':
      if (!vimeo.value?.length) return;
      const vimeoPlayer = vimeo.value[0];

      if (event === 'playVideo') {
        vimeoPlayer?.play?.();
      } else if (event === 'pauseVideo') {
        vimeoPlayer?.pause?.();
      } else {
        const url = props.videos[slideIndex]?.url;
        const id = /([^/]+$)/.exec(url);

        if (!id?.length) break;

        const videoId = parseInt(id[0] || '');
        vimeoPlayer?.update?.(videoId);
      }
      break;
    default:
      console.log('No provider');
  }
}

function onSlide(event: SlideEvent) {
  currentSlide.value = event.currentSlide.index;
  setCurrentProvider();
  isPlaying.value = true;
}

function setCurrentProvider() {
  const slideIndex = currentSlide.value ?? props.selectedItemIndex;
  currentVideoProvider.value = props.videos[slideIndex]?.provider;
}

function onReady() {
  sliderReady.value = true;
  vueperSlides.value?.goToSlide(props.selectedItemIndex);
  setCurrentProvider();
}

function goNext() {
  vueperSlides.value?.next();
}

function goPrevious() {
  vueperSlides.value?.previous();
}

function archiveAddRemove(addOrRemove: 'add' | 'remove') {
  const slideIndex = currentSlide.value ?? props.selectedItemIndex;
  const video = props.videos[slideIndex];
  if (!video) return;

  emit(`archive-${addOrRemove}`, {
    mediaType: 'videos',
    id: video.id,
  });
}

function getYoutubeId(url: string) {
  // Handle various YouTube URL formats including shorts
  const patterns = [
    // Standard watch URLs
    /(?:youtube\.com\/watch\?v=)([^#&?]{11})/,
    // Shortened URLs
    /(?:youtu\.be\/)([^#&?]{11})/,
    // Embed URLs
    /(?:youtube\.com\/embed\/)([^#&?]{11})/,
    // Shorts URLs
    /(?:youtube\.com\/shorts\/)([^#&?]{11})/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return undefined;
}
</script>

<template>
  <div class="flex flex-col items-center justify-end flex-1">
    <VueperSlides
      ref="vueperSlides"
      class="w-full mb-16 no-shadow"
      fixed-height="550px"
      :start-index="props.selectedItemIndex"
      :arrows="false"
      :bullets="false"
      :touchable="true"
      :draggingDistance="300"
      :style="{ width: '950px' }"
      :infinite="false"
      @ready="onReady()"
      @slide="onSlide($event)"
      @autoplay-pause="internalAutoPlaying = false"
      @autoplay-resume="internalAutoPlaying = true"
    >
      <VueperSlide
        v-for="(video, index) in videos"
        :key="index"
        :class="`slide-${index}`"
      >
        <template #content>
          <div class="flex items-center justify-center pointer-events-none">
            <div
              v-if="
                video.provider === 'YOUTUBE' &&
                currentSlide === index &&
                currentVideoProvider === 'YOUTUBE'
              "
            >
              <div v-if="getYoutubeId(video.url)">
                <YoutubeIframe
                  ref="youtube"
                  :video-id="getYoutubeId(video.url)"
                  :width="950"
                  :height="550"
                  :playerVars="playerVars"
                />
              </div>

              <div
                v-else
                class="text-neutral-0"
                :style="{ width: '950px', height: '550px' }"
              >
                <p>Invalid YouTube URL</p>
              </div>
            </div>

            <div
              v-if="
                video.provider === 'VIMEO' &&
                currentSlide === index &&
                currentVideoProvider === 'VIMEO'
              "
            >
              <vueVimeoPlayer
                ref="vimeo"
                :video-id="video.url"
                :player-height="550"
                :player-width="950"
                :autoplay="true"
              >
              </vueVimeoPlayer>
            </div>

            <div
              v-if="
                video.provider === 'DREAMBROKER' &&
                currentSlide === index &&
                currentVideoProvider === 'DREAMBROKER'
              "
            >
              <iframe
                ref="dreambroker"
                class="w-full"
                height="550"
                :src="`${video.url}?autoplay=true`"
                allow="autoplay"
              />
            </div>
          </div>
        </template>
      </VueperSlide>
    </VueperSlides>

    <VideoControls
      v-if="currentVideoProvider !== 'DREAMBROKER'"
      class="mb-[64px]"
      :is-playing="isPlaying"
      @playVideo="handleVideoEvent('playVideo')"
      @pauseVideo="handleVideoEvent('pauseVideo')"
      @resetView="handleVideoEvent('stopVideo')"
    />

    <ArchiveControls
      v-if="sliderReady"
      :allowArchiveRemove="allowArchiveRemove"
      mediaType="videos"
      @archive-add="archiveAddRemove('add')"
      @archive-remove="archiveAddRemove('remove')"
    />

    <CarouselControls
      v-if="sliderReady"
      :total-slides="videos.length"
      :current-slide="(currentSlide ?? 0) + 1"
      media-type="VIDEOS"
      :autoplay="autoPlaying"
      @autoplay-toggled="autoPlaying = !autoPlaying"
      @previous="goPrevious()"
      @next="goNext()"
      @close="$emit('close-preview')"
    />
  </div>
</template>
